
import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { getAdvancedInventoryItems } from '@/services/inventory/inventoryService';
import { Loader2, BarChart } from 'lucide-react';

interface InventoryItem {
  id: string;
  name: string;
  description?: string;
  current_quantity: number;
  minimum_quantity: number;
}

interface InventorySummaryProps {
  storeId: string;
  isLoading: boolean;
  inventory: InventoryItem[];
}

export function InventorySummary({ storeId, isLoading, inventory }: InventorySummaryProps) {
  // Calculate stats based on inventory items
  const lowStockItems = inventory.filter(item => 
    item.current_quantity > 0 && item.current_quantity < item.minimum_quantity
  );
  
  const outOfStockItems = inventory.filter(item => 
    item.current_quantity <= 0
  );
  
  const healthyStockItems = inventory.filter(item =>
    item.current_quantity >= item.minimum_quantity
  );

  return (
    <Card className="p-6">
      <h3 className="text-lg font-medium mb-4 flex items-center">
        <BarChart className="mr-2 h-5 w-5" />
        Advanced Inventory Summary
      </h3>
      
      {isLoading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-muted rounded-lg p-4 text-center">
              <div className="text-4xl font-bold">{inventory.length}</div>
              <div className="text-sm text-muted-foreground mt-1">Total Items</div>
            </div>
            
            <div className="bg-muted rounded-lg p-4 text-center">
              <div className={`text-4xl font-bold ${outOfStockItems.length > 0 ? 'text-destructive' : ''}`}>
                {outOfStockItems.length}
              </div>
              <div className="text-sm text-muted-foreground mt-1">Out of Stock</div>
            </div>
            
            <div className="bg-muted rounded-lg p-4 text-center">
              <div className={`text-4xl font-bold ${lowStockItems.length > 0 ? 'text-amber-500' : ''}`}>
                {lowStockItems.length}
              </div>
              <div className="text-sm text-muted-foreground mt-1">Low Stock</div>
            </div>
          </div>
          
          <div>
            <div className="flex justify-between items-center text-sm mb-2">
              <span>
                <span className="text-green-500 font-medium">
                  {healthyStockItems.length}
                </span> in stock
              </span>
              <span>
                <span className="text-amber-500 font-medium">{lowStockItems.length}</span> low
              </span>
            </div>
            
            <div className="h-4 w-full bg-muted rounded-full overflow-hidden">
              {inventory.length > 0 && (
                <>
                  <div 
                    className="h-4 bg-green-500 float-left" 
                    style={{ 
                      width: `${(healthyStockItems.length / inventory.length) * 100}%` 
                    }}
                  ></div>
                  <div 
                    className="h-4 bg-amber-500 float-left" 
                    style={{ 
                      width: `${(lowStockItems.length / inventory.length) * 100}%` 
                    }}
                  ></div>
                  <div 
                    className="h-4 bg-red-500 float-left" 
                    style={{ 
                      width: `${(outOfStockItems.length / inventory.length) * 100}%` 
                    }}
                  ></div>
                </>
              )}
            </div>
            
            <div className="text-sm mt-4">
              <span className="font-medium">Advanced Inventory Items: </span>
              <span className="text-destructive">{outOfStockItems.length} critical</span>
              {' • '}
              <span className="text-amber-500">{lowStockItems.length} low</span>
              {' • '}
              <span className="text-green-500">
                {healthyStockItems.length} healthy
              </span>
            </div>
          </div>
        </>
      )}
    </Card>
  );
}
